import { Autocomplete, TextField, textFieldVariants } from '@ghs/components';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { $$theme } from 'services/ThemeService/model';
import type { CampaignSetupJobOption, CampaignSetupOption, CreateFormData } from './types';

type Props = {
  formData: Partial<CreateFormData>;
  clientOptions: CampaignSetupOption[];
  campaignOptions: CampaignSetupJobOption[];
  lineOfBusinessOptions: CampaignSetupOption[];
  onChange: (patch: Partial<CreateFormData>) => void;
};

const cloneDateRangeOptions = [
  { id: '30', label: 'Last 30 days' },
  { id: '60', label: 'Last 60 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '180', label: 'Last 180 days' }
];

export default function CampaignDetailsSection({ formData, clientOptions, campaignOptions, lineOfBusinessOptions, onChange }: Props) {
  const { palette } = useUnit($$theme.$palette);

  const requiredSup = (
    <sup style={{ color: palette.error.main, fontSize: '1em' }} aria-label="This is a required field">
      *
    </sup>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Autocomplete
          id="create-client"
          label={<span>{requiredSup} Client</span>}
          placeholder="Select a client..."
          options={clientOptions}
          variant={textFieldVariants.OUTLINED}
          sx={{ width: '100%' }}
          value={clientOptions.find(opt => opt.id === formData.clientId) || null}
          onChange={(_event, value) => onChange({ clientId: value?.id || null })}
          getOptionKey={opt => opt.id}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Stack direction="row" spacing={2} alignItems="stretch">
          {/* Left side: Campaign to Clone label */}
          <Stack spacing={1} sx={{ justifyContent: 'center' }}>
            <Typography variant="body1">Campaign to Clone</Typography>
          </Stack>

          {/* Right side: dropdowns */}
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              id="clone-date-range"
              label="Showing campaigns from"
              placeholder="Select"
              options={cloneDateRangeOptions}
              variant={textFieldVariants.OUTLINED}
              sx={{ width: '100%' }}
              value={cloneDateRangeOptions.find(opt => opt.id === (formData.cloneDateRange ?? '30')) || null}
              onChange={(_event, value) => onChange({ cloneDateRange: (value?.id ?? '30') as CreateFormData['cloneDateRange'] })}
              getOptionKey={opt => opt.id}
            />
          </Box>
        </Stack>
        <Autocomplete
          id="campaign-to-clone"
          label=""
          placeholder="Select a campaign to clone..."
          options={campaignOptions}
          variant={textFieldVariants.OUTLINED}
          sx={{ width: '100%', mt: 2 }}
          value={campaignOptions.find(opt => opt.id === formData.campaignToCloneId) || null}
          onChange={(_event, value) => onChange({ campaignToCloneId: value?.id || null })}
          getOptionKey={opt => opt.id}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          label={<span>{requiredSup} Campaign Name</span>}
          placeholder="Enter campaign name"
          value={formData.campaignName ?? ''}
          onChange={e => onChange({ campaignName: e.target.value })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          label={<span>{requiredSup} Campaign Number</span>}
          placeholder="Enter campaign number"
          value={formData.campaignNumber ?? ''}
          onChange={e => onChange({ campaignNumber: e.target.value })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          id="line-of-business"
          label={<span>{requiredSup} Line of Business</span>}
          placeholder="Select line of business..."
          options={lineOfBusinessOptions}
          variant={textFieldVariants.OUTLINED}
          sx={{ width: '100%' }}
          value={lineOfBusinessOptions.find(opt => opt.id === formData.lineOfBusinessId) || null}
          onChange={(_event, value) => onChange({ lineOfBusinessId: value?.id || null })}
          getOptionKey={opt => opt.id}
        />
      </Grid>
    </Grid>
  );
}
